import { useUserProfileApi } from '@/api/users';
import { FlexCol } from '@/components/ui/flex';
import { Skeleton } from '@/components/ui/skeleton';
import { useSidebar } from '@/hooks/useSidebarContext';
import { useReferenceDetailService } from '@/services/regulatoryWatch';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import { cn } from '@/utils/classUtils';
import { getSourceIconUrl } from '@/utils/source-icon';
import type { ReferenceDetailVersion } from '@ditto/shared';
import { ArrowLeft, ExternalLink, FileText } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { DocumentTypeBadge } from '../components/columns';
import { ReferenceUpdateSummaryCard } from '../components/ReferenceUpdateSummaryCard';

const VersionStatusBadge: FC<{ status: string | null }> = ({ status }) => {
  if (!status) return null;
  const label = status.charAt(0).toUpperCase() + status.slice(1);

  const colorClass =
    status === 'draft'
      ? 'bg-yellow-100 text-neutral-900'
      : status === 'final'
        ? 'bg-green-100 text-neutral-900'
        : status === 'withdrawn'
          ? 'bg-red-100 text-neutral-900'
          : 'bg-neutral-100 text-neutral-600';

  return (
    <span
      className={cn(
        'inline-flex items-center h-6 px-2 rounded-lg text-xs font-medium',
        colorClass
      )}
    >
      {label}
    </span>
  );
};

const VersionCard: FC<{
  version: ReferenceDetailVersion;
  referenceTitle: string;
  isSelected: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSelect: () => void;
  onNavigateToUpdate: (updateId: string) => void;
}> = ({
  version,
  referenceTitle,
  isSelected,
  isFirst,
  isLast,
  onSelect,
  onNavigateToUpdate,
}) => {
  const dateStr = version.update?.publicationDate ?? version.effectiveDate;
  const pubDate = dateStr
    ? new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full text-left transition-colors relative',
        isSelected ? 'bg-yellow-50' : 'hover:bg-neutral-50'
      )}
    >
      <div className="flex px-4">
        {/* Timeline column — single continuous line with dot on top */}
        <div className="w-6 shrink-0 relative">
          {/* Full-height line */}
          <div
            className="absolute left-1/2 -translate-x-1/2 w-px bg-neutral-900"
            style={{
              top: isFirst ? '25px' : 0,
              bottom: isLast ? 'calc(100% - 25px)' : 0,
            }}
          />
          {/* Dot */}
          <div
            className="absolute left-1/2 -translate-x-1/2 z-10"
            style={{ top: 20 }}
          >
            {isSelected ? (
              <div className="size-6 rounded-full bg-yellow-400 border border-neutral-900 flex items-center justify-center">
                <div className="size-2.5 rounded-full bg-neutral-900" />
              </div>
            ) : (
              <div className="size-2.5 rounded-full bg-neutral-900" />
            )}
          </div>
        </div>

        {/* Content */}
        <div
          className={cn(
            'flex-1 min-w-0 py-3 pl-4 pr-0',
            !isLast && 'border-b border-neutral-200'
          )}
        >
          <div className="flex items-start gap-0.5">
            <div className="flex-1 min-w-0">
              <p className="text-base font-medium text-neutral-900 leading-[1.5] truncate">
                {referenceTitle}
              </p>
              {pubDate && (
                <p className="text-xs text-neutral-700 tracking-[0.18px]">
                  <span className="italic">Published:</span>{' '}
                  <span className="font-medium italic">{pubDate}</span>
                </p>
              )}
            </div>
            {version.url && (
              <a
                href={version.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 mt-1"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>

          <div className="mt-0.5">
            <VersionStatusBadge status={version.status} />
          </div>

          {version.update && (
            <div className="mt-2">
              <ReferenceUpdateSummaryCard
                update={version.update}
                onClick={() => onNavigateToUpdate(version.update!.id)}
              />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const VersionViewerPanel: FC<{
  version: ReferenceDetailVersion | null;
}> = ({ version }) => {
  if (!version) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-neutral-400">
        <FileText className="h-12 w-12 text-neutral-300" />
        <span className="text-sm mt-3">Select a version to view</span>
      </div>
    );
  }

  if (!version.sharedFile) {
    return (
      <div className="flex flex-col h-full items-center justify-center text-neutral-400 gap-3">
        <FileText className="h-12 w-12 text-neutral-300" />
        <span className="text-sm">No file available for this version</span>
        {version.url && (
          <a
            href={version.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
          >
            View on source website
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden rounded-2xl bg-white">
      <iframe
        src={version.sharedFile.signedUrl}
        title={version.sharedFile.name}
        className="w-full h-full border-0"
      />
    </div>
  );
};

export const ReferenceDetailPage: FC = () => {
  const { referenceId, workspaceUname } = useParams<{
    referenceId: string;
    workspaceUname: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = `/workspace/${workspaceUname}/regulatory-watch`;
  const locationState = location.state as {
    backPath?: string;
    backLabel?: string;
  } | null;
  const backPath = locationState?.backPath ?? `${basePath}/all-documents`;
  const backLabel = locationState?.backLabel ?? 'My references';

  const { setIsCollapsed } = useSidebar();
  useEffect(() => {
    setIsCollapsed(true);
  }, [setIsCollapsed]);

  const { referenceDetail, isPending } = useReferenceDetailService(referenceId);
  const { data: userProfile } = useUserProfileApi();
  const isDittoUser = userProfile?.type === 'ditto';

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );

  const selectedVersion =
    referenceDetail?.versions.find(v => v.id === selectedVersionId) ?? null;

  useEffect(() => {
    if (referenceDetail?.versions.length && !selectedVersionId) {
      setSelectedVersionId(referenceDetail.versions[0]!.id);
    }
  }, [referenceDetail, selectedVersionId]);

  const handleBack = () => {
    navigate(`${backPath}${location.search}`);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleBack();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNavigateToUpdate = (updateId: string) => {
    navigate(`${basePath}/updates/${updateId}`, {
      state: {
        backPath: location.pathname,
        backLabel: 'Back to reference',
      },
    });
  };

  const sourceIcon = referenceDetail?.source
    ? getSourceIconUrl(referenceDetail.source.id, referenceDetail.source.icon)
    : undefined;
  const sourceFlag = referenceDetail?.source
    ? getGeographyFlag(referenceDetail.source.geography)
    : null;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Top navigation bar */}
      <div className="h-14 border-b border-neutral-200 flex items-center justify-between px-6 bg-white shrink-0">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-700 transition-colors tracking-[0.07px]"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </button>
        {isDittoUser && referenceId && (
          <Link
            to={`/admin/regulatory-watch/references/${referenceId}`}
            className="shrink-0 inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
          >
            See in back office
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden">
        {isPending ? (
          <div className="p-6 flex flex-col gap-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-full max-w-3xl" />
            <Skeleton className="h-4 w-full max-w-2xl" />
            <div className="flex gap-6 mt-4">
              <Skeleton className="h-[500px] w-[378px]" />
              <Skeleton className="h-[500px] flex-1" />
            </div>
          </div>
        ) : referenceDetail ? (
          <div className="flex flex-col h-full">
            {/* Header section */}
            <div className="shrink-0 p-6 bg-white border-b border-[#e4e4e4]">
              <div className="flex items-start gap-6">
                <div className="flex-1 min-w-0 flex flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-[1px] font-medium text-neutral-600">
                      Reference
                    </span>
                  </div>

                  <div className="max-w-[800px]">
                    <h1 className="text-base font-medium text-neutral-900 leading-[1.4]">
                      {referenceDetail.title}
                    </h1>

                    {referenceDetail.description && (
                      <p className="mt-1 text-xs text-neutral-700 leading-[1.5] tracking-[0.18px] max-h-[90px] overflow-hidden">
                        {referenceDetail.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  {referenceDetail.type && (
                    <DocumentTypeBadge type={referenceDetail.type} />
                  )}
                  {referenceDetail.source && (
                    <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-900 tracking-[0.18px]">
                      {sourceFlag && (
                        <img
                          src={sourceFlag}
                          alt=""
                          className="size-[14px] shrink-0 object-contain"
                        />
                      )}
                      {getGeographyLabel(referenceDetail.source.geography)}
                    </span>
                  )}
                  {referenceDetail.source && (
                    <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-medium text-neutral-900 tracking-[0.18px]">
                      {sourceIcon && (
                        <img
                          src={sourceIcon}
                          alt=""
                          className="size-[14px] shrink-0 object-contain rounded-full border border-neutral-200"
                        />
                      )}
                      {referenceDetail.source.title}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Two-column layout: history sidebar (left) + document viewer (right) */}
            <div className="flex flex-1 min-h-0">
              {/* Left: History sidebar */}
              <div className="w-[378px] shrink-0 border-r border-[#e4e4e4] flex flex-col bg-white">
                <div className="px-6 pt-6 pb-4 border-b border-neutral-200 shrink-0">
                  <p className="text-sm font-medium text-black leading-[1.6] tracking-[0.07px]">
                    History
                  </p>
                  <p className="text-xs text-neutral-600 leading-[18px]">
                    {referenceDetail.versions.length} version
                    {referenceDetail.versions.length !== 1 ? 's' : ''} available
                    {referenceDetail.versions.length > 0 &&
                      (() => {
                        const oldest =
                          referenceDetail.versions[
                            referenceDetail.versions.length - 1
                          ]!;
                        const dateStr =
                          oldest.update?.publicationDate ??
                          oldest.effectiveDate;
                        return dateStr
                          ? ` since ${new Date(dateStr).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
                          : '';
                      })()}
                  </p>
                </div>

                <div className="flex flex-col flex-1 overflow-y-auto">
                  {referenceDetail.versions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-neutral-500 gap-2">
                      <FileText className="h-8 w-8 text-neutral-300" />
                      <span className="text-sm">No versions yet</span>
                    </div>
                  ) : (
                    referenceDetail.versions.map((version, idx) => (
                      <VersionCard
                        key={version.id}
                        version={version}
                        referenceTitle={referenceDetail.externalId}
                        isSelected={version.id === selectedVersionId}
                        isFirst={idx === 0}
                        isLast={idx === referenceDetail.versions.length - 1}
                        onSelect={() => setSelectedVersionId(version.id)}
                        onNavigateToUpdate={handleNavigateToUpdate}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Right: Document viewer */}
              <div className="flex-1 min-w-0 bg-neutral-100 pt-6 px-6">
                <VersionViewerPanel version={selectedVersion} />
              </div>
            </div>
          </div>
        ) : (
          <FlexCol
            alignItems="center"
            justifyContent="center"
            className="h-full"
            gap="3"
          >
            <span className="text-sm text-neutral-500">
              Reference not found
            </span>
          </FlexCol>
        )}
      </div>
    </div>
  );
};
